import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Anchor, Mail, Phone, MapPin, Facebook, Instagram, Heart } from 'lucide-react';
import { boatInfo } from '../data/mock';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 py-16 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand Section */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_marine-reserve-app/artifacts/tqyfnfxh_IMG_7293.jpeg"
                alt="The Myli Boat Logo"
                className="h-16 w-auto"
              />
              <div>
                <h3 className="text-2xl font-bold text-white">The Myli Boat</h3>
                <p className="text-xs italic text-blue-200">Vous ne louez pas un bateau, vous vivez une expérience</p>
              </div>
            </div>
            <p className="text-gray-300 mb-4">
              Vivez des moments inoubliables en mer Méditerranée. Location de bateau de luxe avec prestations haut de gamme.
            </p>
            <div className="flex gap-3">
              <a href="#" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Navigation rapide */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Navigation</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => scrollToSection('hero')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Accueil
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('time-slots')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Nos créneaux
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Services & Équipements
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('gallery')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Galerie
                </button>
              </li>
            </ul>
          </div>

          {/* Nos services */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Nos Services</h4>
            <ul className="space-y-3 text-gray-300">
              <li>• Sorties Sunset</li>
              <li>• Demi-journées</li>
              <li>• Journées complètes</li>
              <li>• Prestations culinaires</li>
              <li>• Équipements nautiques</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-400" />
                <span className="text-gray-300 text-sm">{boatInfo.location.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-400" />
                <a href={`tel:${boatInfo.contact.phoneRaw}`} className="text-gray-300 hover:text-white text-sm transition-colors">
                  {boatInfo.contact.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-400" />
                <a href={`mailto:${boatInfo.contact.email}`} className="text-gray-300 hover:text-white text-sm transition-colors">
                  {boatInfo.contact.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Légal */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Informations Légales</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => navigate('/mentions-legales')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Mentions Légales
                </button>
              </li>
              <li>
                <button 
                  onClick={() => navigate('/cgv')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  CGV
                </button>
              </li>
              <li>
                <button 
                  onClick={() => navigate('/politique-confidentialite')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Politique de Confidentialité
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>
              © {currentYear} The Myli Boat. Tous droits réservés.
            </p>
            <p className="flex items-center gap-2">
              Fait avec <Heart className="w-4 h-4 text-red-500 fill-red-500" /> à Marseille
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
